import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
    Stack,
    TextField,
    IconButton,
    InputAdornment,
    CircularProgress,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useAuth } from 'src/utils/Authorization/AuthContext';
import { apiInstance } from 'src/utils/apiAuth';
import { decrypt, encrypt } from 'src/utils/aesUtil';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';

// ----------------------------------------------------------------------

export default function EmpLoginForm() {

    const navigate = useNavigate();

    // Get Creds from URL for GNXT App
    let location = useLocation();
    let params = new URLSearchParams(location.search);
    let username = params.get("username");
    let password = params.get("password");
    let redirectTo = params.get("redirect");

    const [showPassword, setShowPassword] = useState(false);

    const auth = useAuth()

    const EmpLoginSchema = Yup.object().shape({
        // email: Yup.string().email('Email must be a valid email address').required('Email is required'),
        email: Yup.string().required("Username is required."),
        password: Yup.string().required("Password is required")
    });

    const callEmpLoginApi = (email, password) => {
        apiInstance({
            method: 'post',
            url: '/user/login/',
            data: {
                "email": email,
                "password": password
            }
        })
            .then(async function (response) {
                if (response.status === 200) {
                    var data = response.data
                    data.user.active_role = data.user.role[0].role_name;
                    localStorage.setItem('user', encrypt(data.user))
                    localStorage.setItem('authToken', data.key)
                    auth.login(data.user)

                    if (data?.key && data?.key?.length !== 0) {
                        var role_status = data?.user?.role?.[0].is_active
                        var user_status = data?.user?.is_active

                        var role = data?.user?.role?.map(role => role.role_name);
                        var role_statuses = data?.user?.role?.map(role => role.is_active);
                        
                        // Redirect to intended page or dashboard
                        const targetPath = redirectTo || '/dashboard';
                        navigate(targetPath, { replace: true });
                    }

                    // When user key is absent.
                    else {
                        showToast("No user found !!", "warning")
                        navigate('/login', { replace: true });
                    }
                }
            })
            .catch(function (error) {
                if (error) {
                    showErrorToast(error);
                }
            })
            .finally(() => {
                setSubmitting(false)
            })
    }

    const callErpLoginApi = (username, password) => {
        apiInstance({
            method: 'post',
            url: '/erp-login/',
            data: {
                "username": username,
                "password": password
            }
        })
            .then(async function (response) {
                if (response.status === 200) {
                    var data = response.data?.data
                    data.user.active_role = data.user.role[0].role_name;
                    localStorage.setItem('user', encrypt(data.user))
                    localStorage.setItem('authToken', data.key)
                    auth.login(data.user)

                    if (data?.key && data?.key?.length !== 0) {
                        var role_status = data?.user?.role?.[0].is_active
                        var user_status = data?.user?.is_active

                        var role = data?.user?.role?.map(role => role.role_name);
                        var role_statuses = data?.user?.role?.map(role => role.is_active);
                        
                        // Redirect to intended page or dashboard
                        const targetPath = redirectTo || '/dashboard';
                        navigate(targetPath, { replace: true });
                    }

                    // When user key is absent.
                    else {
                        showToast("No user found !!", "warning")
                        navigate('/login', { replace: true });
                    }
                }
            })
            .catch(function (error) {
                if (error) {
                    showErrorToast(error);
                }
            })
            .finally(() => {
                setSubmitting(false)
            })
    }

    const formik = useFormik({
        initialValues: {
            email: localStorage.getItem('employeeEmail') || '',
            password: localStorage.getItem('employeePassword') || '',
            // remember: true
        },
        validationSchema: EmpLoginSchema,
        onSubmit: (formValues) => {

            let email = formValues.email
            let password = formValues.password
            if (email.includes("@")) {
                callEmpLoginApi(email, password)
            } else {
                callErpLoginApi(email, password)
            }
        }
    });

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setSubmitting } = formik;

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    useEffect(() => {
        if (auth?.user?.token) navigate('/dashboard', { replace: true });
        if (username && password) {
            setSubmitting(true);
            callErpLoginApi(username, decrypt(atob(password)));
        }
    }, [])

    return (
        <>
            {username && password && isSubmitting
                ?
                <>
                    <Stack direction='column' justifyContent='center'>
                        <CircularProgress />
                        <Typography>Logging you in to CRM...</Typography>
                    </Stack>
                </>
                :
                <FormikProvider value={formik}>
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                autoComplete="username"
                                label="Username"
                                {...getFieldProps('email')}
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email && errors.email}
                            />

                            <TextField
                                fullWidth
                                autoComplete="current-password"
                                type={showPassword ? 'text' : 'password'}
                                label="Password"
                                {...getFieldProps('password')}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleShowPassword} edge="end">
                                                <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                error={Boolean(touched.password && errors.password)}
                                helperText={touched.password && errors.password}
                            />

                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }} />

                        <LoadingButton
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                            name='emp-login-btn'
                        >
                            Login
                        </LoadingButton>
                    </Form>
                </FormikProvider>
            }
        </>
    );
}